import { endpoint } from "@uri/typed-api";
import { z } from "zod/v4";

const whatsappNumberPattern = /^\d{10,20}(-\d+)?(@(c\.us|g\.us|lid))?$/;

const whatsappNumber = z.string().refine(
  (val) => whatsappNumberPattern.test(val),
  "Must be a valid WhatsApp number or chat ID (10-20 digits, optionally ending with @c.us, @g.us, or @lid)",
);

const valueOutput = <T extends z.ZodTypeAny>(valueSchema: T) =>
  z.union([
    z.object({ success: z.literal(true) }).and(valueSchema),
    z.object({ success: z.literal(false), error: z.string() }),
  ]);

const noValueOutput = z.union([
  z.object({ success: z.literal(true) }),
  z.object({ success: z.literal(false), error: z.string() }),
]);

const token = z.string().describe("Your account's secret authentication token");

const phoneDesc = "Your WhatsApp account phone number";

const apiEndpoint = <
  S extends z.core.$ZodShape,
  O extends z.ZodTypeAny,
>(fields: S, description: string, output: O) =>
  endpoint({
    authRequired: false as const,
    input: z.object({ ...fields, token }).describe(description),
    output,
  });

export const lineServerApiDefinition = {
  sendMessage: apiEndpoint(
    {
      fromNumber: whatsappNumber.describe(phoneDesc),
      toNumber: whatsappNumber.describe(
        "Recipient's WhatsApp ID (e.g., '972501234567@c.us' for individuals or 'group-id@g.us' for groups)",
      ),
      message: z.string().describe("The text message to send"),
      linkPreview: z.boolean().describe(
        "Whether to show link previews for URLs in the message",
      ),
      quotedMessageId: z.string().optional().describe(
        "ID of a message to quote/reply to",
      ),
    },
    "Send a text message to any WhatsApp number or group.",
    valueOutput(z.object({ messageId: z.string() })),
  ),
  sendMedia: apiEndpoint(
    {
      fromNumber: whatsappNumber.describe(phoneDesc),
      toNumber: whatsappNumber.describe("Recipient's WhatsApp ID"),
      base64: z.string().describe(
        "Base64 encoded media data (with or without data URL prefix)",
      ),
      type: z.enum(["image", "video", "document", "audio"]).describe(
        "Media type: 'image', 'video', 'document', or 'audio'",
      ),
      mimeType: z.string().optional().describe(
        "Media MIME type (e.g., 'image/jpeg', 'video/mp4', 'application/pdf'). Auto-detected from data URL if not provided",
      ),
      filename: z.string().optional().describe(
        "Filename for the media. Auto-generated from MIME type if not provided",
      ),
      caption: z.string().optional().describe(
        "Optional caption text for the media",
      ),
    },
    "Send images, videos, or documents with optional caption to any WhatsApp number or group.",
    valueOutput(z.object({ messageId: z.string() })),
  ),
  sendPoll: apiEndpoint(
    {
      fromNumber: whatsappNumber.describe(phoneDesc),
      toNumber: whatsappNumber.describe(
        "Group ID to send the poll to (e.g., '120363123456789012@g.us')",
      ),
      name: z.string().describe("The poll question/title"),
      choices: z.array(z.string()).describe(
        "Array of poll options (minimum 2)",
      ),
      selectableCount: z.number().optional().describe(
        "How many options a voter may select (default: 1)",
      ),
    },
    "Send a poll to a WhatsApp group.",
    valueOutput(z.object({ messageId: z.string() })),
  ),
  editMessage: apiEndpoint(
    {
      fromNumber: whatsappNumber.describe(phoneDesc),
      messageId: z.string().describe(
        "ID of the message to edit (returned from sendMessage)",
      ),
      newText: z.string().describe("The new text for the message"),
    },
    "Edit a previously sent message.",
    noValueOutput,
  ),
  deleteMessage: apiEndpoint(
    {
      fromNumber: whatsappNumber.describe(phoneDesc),
      chatId: z.string().describe(
        "Chat ID (e.g. 972501234567@c.us for 1:1 or groupId@g.us for group)",
      ),
      messageId: z.string().describe("ID of the message to delete"),
    },
    "Delete a previously sent message.",
    noValueOutput,
  ),
  sendReaction: apiEndpoint(
    {
      fromNumber: whatsappNumber.describe(phoneDesc),
      messageId: z.string().describe("ID of the message to react to"),
      reaction: z.string().describe("Reaction emoji to send"),
    },
    "Send a reaction emoji to a message.",
    noValueOutput,
  ),
  sendTypingIndication: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      chatId: z.string().describe(
        "Chat ID where typing indicator should appear",
      ),
      durationMs: z.number().optional().describe(
        "Duration in milliseconds (optional, defaults to standard duration)",
      ),
    },
    "Show the 'typing...' indicator in a chat to make your bot feel more human.",
    noValueOutput,
  ),
  stopTypingIndication: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      chatId: z.string().describe(
        "Chat ID where typing indicator should stop",
      ),
    },
    "Stop showing the 'typing...' indicator in a chat.",
    noValueOutput,
  ),
  getGroups: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      useCache: z.boolean().optional().describe(
        "Whether to use cached group metadata (defaults to true). Set to false for a fresh fetch.",
      ),
    },
    "Retrieve a list of WhatsApp groups with admin status for each group.",
    valueOutput(z.object({
      warning: z.string(),
      cacheMaxAgeSeconds: z.number(),
      groups: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          isAdmin: z.boolean(),
        }),
      ),
    })),
  ),
  addUserToWhatsAppGroup: apiEndpoint(
    {
      groupId: z.string().describe(
        "Group ID (e.g., '120363123456789012@g.us')",
      ),
      userId: z.string().describe(
        "User ID to add (e.g., '972509876543@c.us')",
      ),
      fromNumber: z.string().describe(phoneDesc),
    },
    "Add a user to a WhatsApp group where you are an admin.",
    noValueOutput,
  ),
  removeUserFromWhatsAppGroup: apiEndpoint(
    {
      groupId: z.string().describe(
        "Group ID (e.g., '120363123456789012@g.us')",
      ),
      userId: z.string().describe(
        "User ID to remove (e.g., '972509876543@c.us')",
      ),
      fromNumber: z.string().describe(phoneDesc),
    },
    "Remove a user from a WhatsApp group where you are an admin.",
    noValueOutput,
  ),
  getCommonWhatsappGroups: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      userId: z.string().describe("User ID to check common groups with"),
    },
    "Get a list of WhatsApp groups that both you and another user are members of.",
    valueOutput(z.object({
      groups: z.array(z.object({ id: z.string(), name: z.string() })),
    })),
  ),
  getWhatsappJoinRequests: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      groupId: z.string().describe("Group ID to check for join requests"),
    },
    "Get pending join requests for a group where you are an admin.",
    valueOutput(
      z.object({
        requests: z.array(z.object({
          requesterId: z.string(),
          addedById: z.string().optional(),
          parentGroupId: z.string().optional(),
          method: z.string(),
          timestamp: z.number(),
        })),
      }),
    ),
  ),
  approveWhatsappJoinRequests: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      groupId: z.string().describe(
        "Group ID where requests should be approved",
      ),
      requesterIds: z.array(z.string()).nullable().optional().describe(
        "Array of requester IDs to approve, or null to approve all",
      ),
    },
    "Approve one or more pending join requests for a group.",
    valueOutput(
      z.object({
        results: z.array(z.object({
          requesterId: z.string(),
          error: z.number().nullable(),
        })),
      }),
    ),
  ),
  rejectWhatsappJoinRequests: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      groupId: z.string().describe(
        "Group ID where requests should be rejected",
      ),
      requesterIds: z.array(z.string()).describe(
        "Array of requester IDs to reject",
      ),
    },
    "Reject one or more pending join requests for a group.",
    valueOutput(z.object({
      results: z.array(z.object({
        requesterId: z.string(),
        error: z.number().nullable(),
        message: z.string(),
      })),
    })),
  ),
  getGroupMembers: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      groupId: z.string().describe(
        "Group ID (e.g., '120363123456789012@g.us')",
      ),
    },
    "Get all members of a WhatsApp group, including their admin status. Member IDs may be LID format (e.g., '12345@lid') instead of phone numbers depending on the account's addressing mode. Use the convertLidToNumber endpoint to resolve LIDs to phone numbers.",
    valueOutput(z.object({
      members: z.array(z.object({
        id: z.string(),
        isAdmin: z.boolean(),
        isSuperAdmin: z.boolean(),
      })),
    })),
  ),
  getWhatsappGroupDetails: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      groupLink: z.string().describe(
        "WhatsApp group invite link (e.g., 'https://chat.whatsapp.com/AbCdEfGhIjK')",
      ),
    },
    "Get details about a WhatsApp group using its invite link, including group name, description, size, and participants.",
    valueOutput(
      z.object({
        id: z.string(),
        subject: z.string(),
        desc: z.string(),
        size: z.number(),
        owner: z.string().optional(),
        isInGroup: z.boolean(),
        announce: z.boolean(),
        restrict: z.boolean(),
        creation: z.number(),
        suspended: z.boolean(),
        participants: z.array(z.object({
          id: z.string(),
          isAdmin: z.boolean(),
          isSuperAdmin: z.boolean(),
        })),
      }),
    ),
  ),
  getGroupInviteLink: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      groupId: z.string().describe("Group ID to get the invite link for"),
    },
    "Get the invite link for a WhatsApp group.",
    valueOutput(z.object({ inviteLink: z.string().nullable() })),
  ),
  joinGroupByInviteLink: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      groupLink: z.string().describe("WhatsApp group invite link to join"),
    },
    "Join a WhatsApp group using its invite link.",
    valueOutput(
      z.object({
        status: z.enum(["joined", "pending-approval", "already-joined"]),
        chatId: z.string(),
      }),
    ),
  ),
  blockContact: apiEndpoint(
    {
      fromNumber: whatsappNumber.describe(phoneDesc),
      contactId: whatsappNumber.describe(
        "Contact ID to block (e.g., '972509876543@c.us')",
      ),
    },
    "Block a WhatsApp contact.",
    noValueOutput,
  ),
  reportContact: apiEndpoint(
    {
      fromNumber: whatsappNumber.describe(phoneDesc),
      contactId: whatsappNumber.describe(
        "Contact ID to report as spam (e.g., '972509876543@c.us')",
      ),
    },
    "Report a contact/sender to WhatsApp as spam.",
    noValueOutput,
  ),
  unblockContact: apiEndpoint(
    {
      fromNumber: whatsappNumber.describe(phoneDesc),
      contactId: whatsappNumber.describe(
        "Contact ID to unblock (e.g., '972509876543@c.us')",
      ),
    },
    "Unblock a previously blocked WhatsApp contact.",
    noValueOutput,
  ),
  convertLidToNumber: apiEndpoint(
    {
      fromNumber: z.string().describe(phoneDesc),
      lid: z.string().describe(
        "LID to resolve (e.g., '119846817792023@lid')",
      ),
    },
    "Convert a WhatsApp LID (Linked ID) to a phone number. Some accounts use LID addressing mode where user IDs appear as '12345@lid' instead of phone numbers. This endpoint resolves them.",
    valueOutput(
      z.object({ number: z.string().nullable() }),
    ),
  ),
  setWebhook: apiEndpoint(
    {
      phoneNumber: z.string().describe(phoneDesc),
      url: z.string().or(z.null()).describe(
        "Webhook URL to receive updates, or null to remove webhook",
      ),
    },
    "Set or update the webhook URL to receive incoming messages and events.",
    noValueOutput,
  ),
  addWebhook: apiEndpoint(
    {
      phoneNumber: z.string().describe(phoneDesc),
      url: z.string().describe("Webhook URL to add"),
    },
    "Add an additional webhook URL to receive incoming messages and events. Each account can have multiple webhook URLs — events will be sent to all of them.",
    noValueOutput,
  ),
  removeWebhook: apiEndpoint(
    {
      phoneNumber: z.string().describe(phoneDesc),
      url: z.string().describe("Webhook URL to remove"),
    },
    "Remove a webhook URL from the account. If the URL matches the legacy webhook field, that is also cleared.",
    noValueOutput,
  ),
  reconnect: apiEndpoint(
    { number: z.string().describe(phoneDesc) },
    "Manually trigger a reconnection of your WhatsApp account. The login method (QR code vs pairing code) is determined by the account's preferCodeLogin setting — use setLoginMethod to change it before reconnecting.",
    noValueOutput,
  ),
  setLoginMethod: apiEndpoint(
    {
      phoneNumber: z.string().describe(phoneDesc),
      preferCodeLogin: z.boolean().describe(
        "Set to true for pairing code login, false for QR code login",
      ),
    },
    "Set whether the account should use a pairing code or QR code when reconnecting. When preferCodeLogin is true, the next reconnect will generate a pairing code instead of a QR code.",
    noValueOutput,
  ),
  clearCredentials: apiEndpoint(
    { phoneNumber: z.string().describe(phoneDesc) },
    "Clear all stored credentials for your WhatsApp account. Use this to reset authentication.",
    noValueOutput,
  ),
  changeWhatsappAccountName: apiEndpoint(
    {
      phoneNumber: z.string().describe(phoneDesc),
      name: z.string().describe(
        "The new display name for your WhatsApp account",
      ),
    },
    "Change the display name of your WhatsApp account.",
    noValueOutput,
  ),
  changeWhatsappAccountAvatar: apiEndpoint(
    {
      phoneNumber: z.string().describe(phoneDesc),
      imageBase64: z.string().describe(
        "Base64-encoded JPEG image. Can optionally include data URI prefix (data:image/jpeg;base64,...)",
      ),
    },
    "Change the profile picture of your WhatsApp account.",
    noValueOutput,
  ),
  changeWhatsappAccountBio: apiEndpoint(
    {
      phoneNumber: z.string().describe(phoneDesc),
      bio: z.string().describe("The new bio/about text for your account"),
    },
    "Change the bio/about text of your WhatsApp account.",
    noValueOutput,
  ),
  changeWhatsappPrivacySettings: apiEndpoint(
    {
      phoneNumber: z.string().describe(phoneDesc),
      category: z.enum(["about", "profilePic"]).describe(
        "The privacy category to update ('about' or 'profilePic')",
      ),
      value: z.enum(["all", "contacts", "none"]).describe(
        "Who can see this information ('all' for Everyone, 'contacts' for My Contacts, 'none' for Nobody)",
      ),
    },
    "Change the privacy settings of your WhatsApp account.",
    noValueOutput,
  ),
} as const;

const telegramNumber = z.string().describe(
  "Your Telegram account phone number",
);

export const telegramApiDefinition = {
  submitTelegramCode: apiEndpoint(
    {
      phoneNumber: telegramNumber,
      code: z.string().describe("Login code received in Telegram"),
    },
    "Submit the login code to complete Telegram authentication.",
    noValueOutput,
  ),
  sendTelegramMessage: apiEndpoint(
    {
      phoneNumber: telegramNumber,
      chatId: z.string().describe("Chat ID or username to send to"),
      text: z.string().describe("Message text"),
    },
    "Send a text message via Telegram.",
    valueOutput(z.object({ messageId: z.string() })),
  ),
  getTelegramGroups: apiEndpoint(
    {
      phoneNumber: telegramNumber,
    },
    "Retrieve a list of Telegram groups and channels.",
    valueOutput(z.object({
      warning: z.string(),
      cacheMaxAgeSeconds: z.number(),
      groups: z.array(
        z.object({ id: z.string(), name: z.string(), isAdmin: z.boolean() }),
      ),
    })),
  ),
  joinTelegramGroup: apiEndpoint(
    {
      phoneNumber: telegramNumber,
      groupUsername: z.string().describe("Group/channel username to join"),
    },
    "Join a Telegram group or channel by username.",
    valueOutput(
      z.object({ status: z.enum(["joined"]), chatId: z.string() }),
    ),
  ),
  setTelegramWebhook: apiEndpoint(
    {
      phoneNumber: telegramNumber,
      url: z.string().or(z.null()).describe(
        "Webhook URL to receive updates, or null to remove webhook",
      ),
    },
    "Set or update the webhook URL for a Telegram account.",
    noValueOutput,
  ),
  addTelegramWebhook: apiEndpoint(
    {
      phoneNumber: telegramNumber,
      url: z.string().describe("Webhook URL to add"),
    },
    "Add an additional webhook URL for a Telegram account.",
    noValueOutput,
  ),
  removeTelegramWebhook: apiEndpoint(
    {
      phoneNumber: telegramNumber,
      url: z.string().describe("Webhook URL to remove"),
    },
    "Remove a webhook URL from a Telegram account.",
    noValueOutput,
  ),
} as const;

export const userApiDefinition = {
  createAccount: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      phoneNumber: z.string(),
      name: z.string().optional(),
      notes: z.string().optional(),
    }),
    output: z.union([
      z.object({ secretToken: z.string(), accountId: z.string() }),
      z.object({ error: z.string() }),
    ]),
  }),
  setWarmupParticipation: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      phoneNumber: z.string().describe(phoneDesc),
      enabled: z.boolean(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  deleteAccount: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      phoneNumber: z.string().describe(phoneDesc),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  getAvailableProxyCountries: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      phoneNumber: z.string().describe(phoneDesc),
    }),
    output: z.union([
      z.object({ countries: z.array(z.string()) }),
      z.object({ error: z.string() }),
    ]),
  }),
  setProxyCountry: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      phoneNumber: z.string().describe(phoneDesc),
      country: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  createCheckoutUrl: endpoint({
    authRequired: false,
    input: z.object({ refreshToken: z.string() }),
    output: z.union([
      z.object({ url: z.string() }),
      z.object({ error: z.string() }),
    ]),
  }),
  createBillingPortalUrl: endpoint({
    authRequired: false,
    input: z.object({ refreshToken: z.string() }),
    output: z.union([
      z.object({ url: z.string() }),
      z.object({ error: z.string() }),
    ]),
  }),
  createPbxmeNumber: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      countryId: z.string().optional().describe(
        "pbxme country id. Defaults to Israel (93) if omitted.",
      ),
    }),
    output: z.union([
      z.object({ success: z.literal(true), phone: z.string() }),
      z.object({ error: z.string() }),
    ]),
  }),
  listPbxmeCountries: endpoint({
    authRequired: false,
    input: z.object({ refreshToken: z.string() }),
    output: z.union([
      z.object({
        countries: z.array(z.object({
          countryId: z.string(),
          iso: z.string(),
          name: z.string(),
          priceUsd: z.number(),
          available: z.number(),
        })),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
  getPbxmeCountryAvailability: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      countryId: z.string(),
    }),
    output: z.union([
      z.object({
        available: z.number(),
        priceUsd: z.number(),
        sampleNumber: z.string().optional(),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
  releaseNumber: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      phoneNumber: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  createTelegramAccount: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      phoneNumber: z.string(),
      apiId: z.number(),
      apiHash: z.string(),
      sessionString: z.string().optional(),
    }),
    output: z.union([
      z.object({ secretToken: z.string(), accountId: z.string() }),
      z.object({ error: z.string() }),
    ]),
  }),
  submitTelegramCode: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      phoneNumber: z.string(),
      code: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  deleteTelegramAccount: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      phoneNumber: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  createFbAccount: endpoint({
    authRequired: false,
    input: z.object({ refreshToken: z.string() }),
    output: z.union([
      z.object({
        success: z.literal(true),
        fbAccountId: z.string(),
        secretToken: z.string(),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
  releaseFbAccount: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      fbAccountId: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  getFbAccountLoginUrl: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      fbAccountId: z.string(),
    }),
    output: z.union([
      z.object({
        success: z.literal(true),
        loginUrl: z.string(),
        sessionId: z.string(),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
  closeFbAccountLoginSession: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      sessionId: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  addFbGroup: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      fbAccountId: z.string(),
      groupId: z.string(),
      name: z.string().optional(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  removeFbGroup: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      fbGroupId: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  setFbAccountWebhooks: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      fbAccountId: z.string(),
      webhookUrls: z.array(z.string()),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  commentOnFbPost: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      fbAccountId: z.string(),
      groupId: z.string(),
      postId: z.string(),
      text: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  sendFbDirectMessage: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      fbAccountId: z.string(),
      recipientUserId: z.string(),
      text: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  getAdminStats: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
    }),
    output: z.union([
      z.object({
        success: z.literal(true),
        stats: z.object({
          numbersCount: z.number(),
          connectedWhatsappCount: z.number(),
          totalWhatsappCount: z.number(),
          connectedTelegramCount: z.number(),
          totalTelegramCount: z.number(),
          totalUsersCount: z.number(),
        }),
        whatsappAccounts: z.array(z.object({
          phoneNumber: z.string(),
          name: z.string(),
          isConnected: z.boolean(),
          isEnabled: z.boolean(),
          admins: z.array(z.string()),
          billingUser: z.string().nullable(),
        })),
        telegramAccounts: z.array(z.object({
          phoneNumber: z.string(),
          name: z.string(),
          isConnected: z.boolean(),
          isEnabled: z.boolean(),
          admins: z.array(z.string()),
          billingUser: z.string().nullable(),
        })),
        phoneNumbers: z.array(z.object({
          number: z.string(),
          status: z.string().nullable(),
          createdAt: z.number(),
          admins: z.array(z.string()),
          billingUser: z.string().nullable(),
        })),
        fbAccounts: z.array(z.object({
          id: z.string(),
          status: z.string().nullable(),
          admins: z.array(z.string()),
          billingUser: z.string().nullable(),
        })),
        users: z.array(z.object({
          email: z.string(),
          lastSeen: z.number(),
          createdAt: z.number(),
          monthlyPayment: z.number().nullable(),
          paidCapacity: z.number().nullable(),
          stripeCustomerId: z.string().nullable(),
        })),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
} as const;

export const adminApiDefinition = {
  getPaidCapacity: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      adminToken: z.string(),
    }),
    output: valueOutput(
      z.object({ paidCapacity: z.number() }),
    ),
  }),
  setPaidCapacity: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      paidCapacity: z.number(),
      adminToken: z.string(),
    }),
    output: noValueOutput,
  }),
  provisionAccount: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      phoneNumber: z.string(),
      preferCodeLogin: z.boolean().optional(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ secretToken: z.string(), accountId: z.string() }),
      z.object({ error: z.string() }),
    ]),
  }),
  adminReconnect: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      email: z.string(),
      adminToken: z.string(),
    }),
    output: noValueOutput,
  }),
  adminDeleteAccount: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      email: z.string(),
      adminToken: z.string(),
    }),
    output: noValueOutput,
  }),
  adminDeleteWhatsappLine: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      email: z.string(),
      adminToken: z.string(),
    }),
    output: noValueOutput,
  }),
  adminSetLoginMethod: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      email: z.string(),
      preferCodeLogin: z.boolean(),
      adminToken: z.string(),
    }),
    output: noValueOutput,
  }),
  getAccountStatus: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      email: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({
        isConnected: z.boolean(),
        pairingCode: z.string().nullable(),
        verificationCode: z.string().nullable(),
        qrIssuedAt: z.number().nullable(),
        preferCodeLogin: z.boolean(),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
  provisionTelegramAccount: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      phoneNumber: z.string(),
      apiId: z.number(),
      apiHash: z.string(),
      sessionString: z.string().optional(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ secretToken: z.string(), accountId: z.string() }),
      z.object({ error: z.string() }),
    ]),
  }),
  adminDeleteTelegramAccount: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      email: z.string(),
      adminToken: z.string(),
    }),
    output: noValueOutput,
  }),
  getTelegramAccountStatus: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      email: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ isConnected: z.boolean() }),
      z.object({ error: z.string() }),
    ]),
  }),
  adminSubmitTelegramCode: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      code: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  activateAccount: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      phoneNumber: z.string(),
      countryCode: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({
        activatorUrl: z.string(),
        vmName: z.string(),
        accountId: z.string(),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
  teardownActivator: endpoint({
    authRequired: false,
    input: z.object({
      vmName: z.string(),
      adminToken: z.string(),
    }),
    output: noValueOutput,
  }),
  adminCreateFbAccount: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({
        success: z.literal(true),
        fbAccountId: z.string(),
        secretToken: z.string(),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
  adminGetFbAccountLoginUrl: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      fbAccountId: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({
        success: z.literal(true),
        loginUrl: z.string(),
        sessionId: z.string(),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
  adminCloseFbAccountLoginSession: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      sessionId: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  adminSetFbAccountWebhooks: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      fbAccountId: z.string(),
      webhookUrls: z.array(z.string()),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  adminAddFbGroup: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      fbAccountId: z.string(),
      groupId: z.string(),
      name: z.string().optional(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({
        success: z.literal(true),
        fbGroupId: z.string(),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
  adminRemoveFbGroup: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      fbAccountId: z.string(),
      groupId: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  adminCommentOnFbPost: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      fbAccountId: z.string(),
      groupId: z.string(),
      postId: z.string(),
      text: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  adminSendFbDirectMessage: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      fbAccountId: z.string(),
      recipientUserId: z.string(),
      text: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
} as const;

export const fullApiDefinition = {
  ...lineServerApiDefinition,
  ...telegramApiDefinition,
  ...adminApiDefinition,
  ...userApiDefinition,
} as const;

export type LineServerApi = typeof lineServerApiDefinition;
export type AdminApi = typeof adminApiDefinition;
export type UserApi = typeof userApiDefinition;
export type FullApi = typeof fullApiDefinition;
